import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";

const QUICK_LINKS = [
  { icon: "bi-laptop-fill",      label: "My Assets",      href: "/myassets",        color: "#3d4c7a", bg: "#eef0f8" },
  { icon: "bi-check2-square",    label: "Approval Queue", href: "/asset-approvals", color: "#16a34a", bg: "#f0fdf4" },
];

const PRIORITY_COLOR = {
  Critical: { color: "#7c3aed", bg: "#f5f3ff" },
  High:     { color: "#dc2626", bg: "#fef2f2" },
  Medium:   { color: "#d97706", bg: "#fffbeb" },
  Low:      { color: "#16a34a", bg: "#f0fdf4" },
};

export default function ManagerAssetLanding() {
  const navigate  = useNavigate();
  const token      = localStorage.getItem("token");
  const employeeId = localStorage.getItem("employeeId");

  const [myAssets,   setMyAssets]   = useState([]);
  const [pending,    setPending]    = useState([]);
  const [history,    setHistory]    = useState([]);
  const [loading,    setLoading]    = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [allocRes, reqRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/asset-allocations/employee/${employeeId}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/api/asset-requests/manager/${employeeId}`,     { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      if (allocRes.ok) {
        const d = await allocRes.json();
        setMyAssets((Array.isArray(d) ? d : d.content || []).filter(a => a.status === "ACTIVE"));
      }

      if (reqRes.ok) {
        const d = await reqRes.json();
        const all = Array.isArray(d) ? d : d.content || [];
        setPending(all.filter(r => (r.status || "").toLowerCase() === "pending"));
        setHistory(all.filter(r => (r.status || "").toLowerCase() !== "pending"));
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [token, employeeId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const s = document.createElement("style");
    s.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&display=swap');
      .mal-root * { box-sizing: border-box; }
      .mal-root { font-family: 'Sora', sans-serif; background: #f4f6fb; min-height: 100vh; color: #1a1f36; }
      .mal-tile { background:#fff; border:1.5px solid #e8eaf0; border-radius:16px; padding:32px 24px;
        display:flex; flex-direction:column; align-items:center; gap:14px; cursor:pointer;
        transition:all 0.18s ease; text-align:center; flex:1; min-width:200px; }
      .mal-tile:hover { border-color:#080235; box-shadow:0 8px 28px rgba(8,2,53,0.12); transform:translateY(-3px); }
      .mal-tile-icon { width:56px; height:56px; border-radius:14px; display:flex; align-items:center; justify-content:center; font-size:24px; }
      .mal-stat { background:#fff; border:1px solid #e8eaf0; border-radius:14px; padding:22px 28px;
        display:flex; align-items:center; gap:18px; flex:1; min-width:170px; }
      .mal-tr:hover td { background:#f8f9fc; }
      @keyframes spin { to { transform:rotate(360deg); } }
      .mal-spin { width:28px; height:28px; border-radius:50%; border:3px solid #e8eaf0; border-top-color:#080235; animation:spin 0.7s linear infinite; margin:60px auto; }
    `;
    document.head.appendChild(s);
    const ic = document.createElement("link");
    ic.rel = "stylesheet";
    ic.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
    document.head.appendChild(ic);
    return () => { s.remove(); ic.remove(); };
  }, []);

  const STATS = [
    { icon: "bi-laptop-fill",    label: "My Active Assets",    val: myAssets.length, iconBg: "#eef0f8", iconColor: "#3d4c7a" },
    { icon: "bi-hourglass-split", label: "Pending Approvals",  val: pending.length,  iconBg: "#fffbeb", iconColor: "#d97706" },
    { icon: "bi-check-circle-fill", label: "Processed",        val: history.length,  iconBg: "#f0fdf4", iconColor: "#16a34a" },
  ];

  return (
    <div className="mal-root">
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000 }}>
        <AssetModuleNavbar />
      </div>

      <div style={{ padding: "24px 32px 48px", paddingTop: 130 }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 32 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: "#080235", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className="bi bi-laptop-fill" style={{ color: "#fff", fontSize: 22 }} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#080235" }}>Asset Portal</h1>
            <p style={{ margin: 0, fontSize: 13, color: "#8892a4", marginTop: 2 }}>Manage your assets and review your team's requests</p>
          </div>
        </div>

        {/* Stats */}
        {loading ? <div className="mal-spin" /> : (
          <>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 32 }}>
              {STATS.map(({ icon, label, val, iconBg, iconColor }) => (
                <div className="mal-stat" key={label}>
                  <div style={{ width: 46, height: 46, borderRadius: 12, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <i className={`bi ${icon}`} style={{ color: iconColor, fontSize: 21 }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1, color: "#1a1f36" }}>{val}</div>
                    <div style={{ fontSize: 11, fontWeight: 500, color: "#8892a4", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: 4 }}>{label}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Quick Access */}
            <div style={{ marginBottom: 32 }}>
              <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", color: "#8892a4", marginBottom: 14 }}>Quick Access</p>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                {QUICK_LINKS.map(({ icon, label, href, color, bg }) => (
                  <div key={href} className="mal-tile" onClick={() => navigate(href)}>
                    <div className="mal-tile-icon" style={{ background: bg }}>
                      <i className={`bi ${icon}`} style={{ color }} />
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1f36" }}>{label}</span>
                    <span style={{ fontSize: 12, color: "#8892a4" }}>
                      {href === "/myassets" ? `${myAssets.length} active` : `${pending.length} pending`}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Pending Requests Preview */}
            <div style={{ background: "#fff", border: "1px solid #e8eaf0", borderRadius: 16, overflow: "hidden" }}>
              <div style={{ padding: "18px 24px", borderBottom: "1px solid #f0f2f7", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "#1a1f36" }}>Team Pending Requests</p>
                  {pending.length > 0 && (
                    <span style={{ background: "#fef2f2", color: "#dc2626", fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20, border: "1px solid #fecaca" }}>
                      {pending.length}
                    </span>
                  )}
                </div>
                <button onClick={() => navigate("/asset-approvals")}
                  style={{ background: "#080235", color: "#fff", border: "none", borderRadius: 8, padding: "7px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  View All
                </button>
              </div>

              {pending.length === 0 ? (
                <div style={{ textAlign: "center", padding: "48px 24px", color: "#a0a9bf" }}>
                  <i className="bi bi-check2-all" style={{ fontSize: 32, opacity: 0.4 }} />
                  <p style={{ margin: "12px 0 0", fontSize: 13 }}>No pending requests from your team</p>
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: "#f8f9fc" }}>
                        {["Employee", "Asset", "Priority", "Expected By", "Justification"].map(h => (
                          <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "#8892a4", textTransform: "uppercase", letterSpacing: "0.6px", borderBottom: "1px solid #e8eaf0" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pending.slice(0, 5).map(r => {
                        const pc = PRIORITY_COLOR[r.priority] || { color: "#5a6482", bg: "#f4f6fb" };
                        return (
                          <tr key={r.id} className="mal-tr" style={{ borderBottom: "1px solid #f0f2f7" }}>
                            <td style={{ padding: "12px 16px", fontWeight: 600, color: "#1a1f36" }}>{r.employeeName || "—"}</td>
                            <td style={{ padding: "12px 16px", color: "#3d4c7a", fontWeight: 500 }}>{r.assetCategory || "—"}</td>
                            <td style={{ padding: "12px 16px" }}>
                              {r.priority
                                ? <span style={{ padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, color: pc.color, background: pc.bg }}>{r.priority}</span>
                                : <span style={{ color: "#c0c5d4" }}>—</span>}
                            </td>
                            <td style={{ padding: "12px 16px", color: "#5a6482" }}>{r.expectedDate || "—"}</td>
                            <td style={{ padding: "12px 16px", color: "#8892a4", maxWidth: 200 }}>
                              <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.justification || "—"}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
